import React from 'react';
import { User, Mail, MessageSquare } from 'lucide-react';
import './HumanHandoff.css'; // Keeping for potential custom tweaks, but mostly using Tailwind

const HumanHandoff = ({
    isEditMode,
    humanHandoff,
    setHumanHandoff,
    updateConfig,
    emailIntegrations,
    setActiveSubTab
}) => {
    // Default structures if undefined
    const handoffConfig = humanHandoff || {
        enabled: false,
        allowUserInitiated: true,
        adminEmails: '', // Changed from adminEmail to match backend
        slackWebhookUrl: '',
        timeoutMinutes: 5,
        timeoutSeconds: 60,
        timeoutMessage: 'Sorry, none of our representatives are available at the moment, please try again later.',
        missedRequestAction: 'email_ticket'
    };

    const handleHandoffChange = (key, value) => {
        const newConfig = { ...handoffConfig, [key]: value };
        setHumanHandoff(newConfig);
        updateConfig({ humanHandoff: newConfig });
    };

    return (
        <div className="flex h-full w-full bg-slate-50/50 p-3 gap-3 overflow-hidden">
            <div className="w-full h-full flex flex-col bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-slate-100 bg-white sticky top-0 z-10">
                    <h2 className="text-lg font-bold tracking-tight text-slate-900">Human Handoff</h2>
                    <p className="text-xs text-slate-500 font-medium mt-0.5">Configure how and when users can reach a human agent.</p>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    <div className="max-w-3xl mx-auto px-8 py-8 space-y-10">

                        {/* 1. General Settings */}
                        <div className="space-y-6">
                            <div className="flex items-start gap-3">
                                <div className="mt-0.5 p-1.5 bg-indigo-50 text-indigo-600 rounded-lg">
                                    <User size={18} />
                                </div>
                                <div className="space-y-1">
                                    <h3 className="text-sm font-semibold text-slate-900">Allow Human Handoff</h3>
                                    <p className="text-xs text-slate-500">Enable and configure the handoff experience.</p>
                                </div>
                                <div className="ml-auto">
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={handoffConfig.enabled}
                                            onChange={(e) => handleHandoffChange('enabled', e.target.checked)}
                                            className="sr-only peer"
                                        />
                                        <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-500"></div>
                                    </label>
                                </div>
                            </div>

                            {handoffConfig.enabled && (
                                <div className="pl-11 space-y-6">
                                    {/* User Initiated Toggle */}
                                    <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl">
                                        <div className="space-y-0.5">
                                            <span className="text-sm font-medium text-slate-700 block">User-Initiated Handoff</span>
                                            <span className="text-xs text-slate-400">Show "Talk to Human" button in the widget menu</span>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={handoffConfig.allowUserInitiated}
                                                onChange={(e) => handleHandoffChange('allowUserInitiated', e.target.checked)}
                                                className="sr-only peer"
                                            />
                                            <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-500"></div>
                                        </label>
                                    </div>

                                    {/* Notification Email Selection */}
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <label className="text-xs font-semibold text-slate-700 flex items-center gap-2">
                                                <Mail size={12} className="text-slate-400" />
                                                Notification Email
                                            </label>
                                            {emailIntegrations && emailIntegrations.length > 0 && (
                                                <button
                                                    onClick={() => setActiveSubTab && setActiveSubTab('Integration')}
                                                    className="text-[10px] text-indigo-600 hover:text-indigo-700 font-medium cursor-pointer"
                                                >
                                                    Manage Emails
                                                </button>
                                            )}
                                        </div>

                                        <div className="space-y-2">
                                            <input
                                                type="text"
                                                value={handoffConfig.adminEmails || ''}
                                                onChange={(e) => handleHandoffChange('adminEmails', e.target.value)}
                                                placeholder="bruce@wayne-enterprises.com, alfred@..."
                                                className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all placeholder:text-slate-400"
                                            />
                                            <p className="text-[10px] text-slate-400">
                                                Enter email addresses to notify, separated by commas.
                                                {(!emailIntegrations || emailIntegrations.length === 0) && (
                                                    <span className="block mt-1 text-slate-500 italic">
                                                        Using System Default Sender (notifications@smushlabs.ai).
                                                    </span>
                                                )}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Slack Webhook */}
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-slate-700 flex items-center gap-2">
                                            <MessageSquare size={12} className="text-slate-400" />
                                            Slack Webhook URL (Optional)
                                        </label>
                                        <input
                                            type="text"
                                            value={handoffConfig.slackWebhookUrl || ''}
                                            onChange={(e) => handleHandoffChange('slackWebhookUrl', e.target.value)}
                                            placeholder="https://hooks.slack.com/services/..."
                                            className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all placeholder:text-slate-400"
                                        />
                                        <p className="text-[10px] text-slate-400">Post notifications to a specific Slack channel.</p>
                                    </div>

                                    {/* Wait Timeout */}
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-slate-700 flex items-center gap-2">
                                            Wait Timeout (seconds)
                                        </label>
                                        <input
                                            type="number"
                                            value={handoffConfig.timeoutSeconds !== undefined ? handoffConfig.timeoutSeconds : 60}
                                            onChange={(e) => handleHandoffChange('timeoutSeconds', parseInt(e.target.value) || 0)}
                                            className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all"
                                        />
                                        <p className="text-[10px] text-slate-400">Time to wait for an agent before sending the timeout message.</p>
                                    </div>

                                    {/* Timeout Message */}
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-slate-700 flex items-center gap-2">
                                            Timeout Message
                                        </label>
                                        <textarea
                                            value={handoffConfig.timeoutMessage || 'Sorry, none of our representatives are available at the moment, please try again later.'}
                                            onChange={(e) => handleHandoffChange('timeoutMessage', e.target.value)}
                                            className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all placeholder:text-slate-400"
                                            rows="3"
                                        />
                                        <p className="text-[10px] text-slate-400">Message sent to the user if an agent doesn't reply within the timeout period.</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HumanHandoff;
